import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ProductSummaryView, TemplateConfig, TemplateVersionView } from '@lp/contracts';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { errorText, fieldProblems, get, post, put } from '@/lib/api';
import { useT } from '@/lib/i18n';
import { DeleteDraftButton } from '@/components/delete-draft';
import { SettingsEditor } from '@/components/settings';
import { LevelsEditor } from '@/components/levels-editor';
import { Button, Card, Dialog, ErrorBox, Field, Input, Loading, PageHeader } from '@/components/ui';

/** the draft editor of a template: its products (with a company's own names) and its settings — then publish */
export function TemplateEditPage() {
  const t = useT();
  const code = (useParams().code ?? '').toUpperCase();
  const version = Number(useParams().version);
  const nav = useNavigate();
  const qc = useQueryClient();
  const draft = useQuery({ queryKey: ['template', code, 'version', version], queryFn: () => get<TemplateVersionView>(`/platform/templates/${code}/versions/${version}`) });
  const catalog = useQuery({ queryKey: ['products'], queryFn: () => get<ProductSummaryView[]>('/platform/products') });
  const [config, setConfig] = useState<TemplateConfig | null>(null);
  const [note, setNote] = useState('');
  const [dirty, setDirty] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [confirm, setConfirm] = useState('');
  useEffect(() => {
    if (draft.data && !config) {
      setConfig(draft.data.config);
      setNote(draft.data.note ?? '');
    }
  }, [draft.data, config]);
  const change = (c: TemplateConfig) => {
    setConfig(c);
    setDirty(true);
  };
  const save = useMutation({
    mutationFn: () => put<TemplateVersionView>(`/platform/templates/${code}/versions/${version}`, { config, ...(note.trim() ? { note } : {}) }),
    onSuccess: (v) => {
      qc.setQueryData(['template', code, 'version', version], v);
      setDirty(false);
    },
  });
  const publish = useMutation({
    mutationFn: () => post(`/platform/templates/${code}/versions/${version}/publish`, { confirmCode: confirm }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['template', code] });
      void qc.invalidateQueries({ queryKey: ['templates'] });
      nav(`/templates/${code}`);
    },
  });

  if (draft.isLoading || !config) return draft.error ? <ErrorBox text={errorText(draft.error)} /> : <Loading />;
  if (draft.data?.status !== 'draft') return <ErrorBox text={t('This version is published and never changes. Make a new version to change the template.')} />;

  const has = (p: string) => config.products.some((x) => x.code === p);
  const toggle = (p: string) => change({ ...config, products: has(p) ? config.products.filter((x) => x.code !== p) : [...config.products, { code: p }] });
  const setName = (p: string, k: 'nameEn' | 'nameKm', v: string) => change({ ...config, products: config.products.map((x) => (x.code === p ? { ...x, [k]: v.trim() ? v : undefined } : x)) });

  return (
    <>
      <Link to={`/templates/${code}`} className="mb-2 inline-flex items-center gap-1 text-xs text-ink-500 hover:text-ink-800">
        <ArrowLeft size={12} /> {code}
      </Link>
      <PageHeader
        title={`${code} · ${t('Version')} ${version} · ${t('Draft')}`}
        subtitle={t('Nothing here is in force until it is published. Companies move to the new version from the template page.')}
        actions={
          <>
            <DeleteDraftButton
              path={`/platform/templates/${code}/versions/${version}`}
              label={`${code} v${version}`}
              onDeleted={(a) => {
                void qc.invalidateQueries({ queryKey: ['template', code] });
                void qc.invalidateQueries({ queryKey: ['templates'] });
                nav((a as { templateDeleted?: boolean } | undefined)?.templateDeleted ? '/templates' : `/templates/${code}`);
              }}
            />
            <Button variant="secondary" onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
              {t('Save')}
            </Button>
            <Button onClick={() => setPublishing(true)} disabled={dirty}>
              {t('Publish')}
            </Button>
          </>
        }
      />
      {save.error && <ErrorBox text={[errorText(save.error), ...fieldProblems(save.error)].join(' — ')} />}

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title={t('Products')}>
          <ul className="space-y-2">
            {(catalog.data ?? []).map((p) => (
              <li key={p.code}>
                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={has(p.code)} onChange={() => toggle(p.code)} aria-label={p.code} />
                  <span className="font-mono text-xs">{p.code}</span> <span className="text-ink-600">{p.nameEn}</span>
                </label>
                {has(p.code) && (
                  <div className="ml-6 mt-1 flex flex-wrap gap-2">
                    <Input value={config.products.find((x) => x.code === p.code)?.nameEn ?? ''} onChange={(e) => setName(p.code, 'nameEn', e.target.value)} placeholder={t('Own name (English) — optional')} className="h-8 w-52 text-xs" maxLength={80} />
                    <Input value={config.products.find((x) => x.code === p.code)?.nameKm ?? ''} onChange={(e) => setName(p.code, 'nameKm', e.target.value)} placeholder={t('Own name (Khmer) — optional')} className="h-8 w-52 text-xs" maxLength={80} />
                  </div>
                )}
              </li>
            ))}
          </ul>
        </Card>
        <Card title={t('Settings')}>
          <SettingsEditor map={config.settings} products={(catalog.data ?? []).map((p) => p.code)} onChange={(settings) => change({ ...config, settings })} />
        </Card>
      </div>

      <Card title={t('Levels')} className="mt-4">
        <LevelsEditor levels={config.levels} onChange={(levels) => change({ ...config, levels })} />
      </Card>

      <Card title={t('Note')} className="mt-4">
        <Input
          value={note}
          onChange={(e) => {
            setNote(e.target.value);
            setDirty(true);
          }}
          maxLength={300}
          placeholder={t('What changed in this version')}
        />
      </Card>

      {publishing && (
        <Dialog
          title={`${t('Publish')} ${code} v${version}`}
          onClose={() => setPublishing(false)}
          footer={
            <>
              <Button variant="secondary" onClick={() => setPublishing(false)}>
                {t('Cancel')}
              </Button>
              <Button onClick={() => publish.mutate()} disabled={publish.isPending || confirm.trim().toUpperCase() !== code}>
                {t('Publish')}
              </Button>
            </>
          }
        >
          <p>{t('A published version never changes. Companies stay on their version until you move them.')}</p>
          <Field label={`${t('Type the template code to confirm')}: ${code}`}>
            <Input value={confirm} onChange={(e) => setConfirm(e.target.value)} autoFocus />
          </Field>
          {publish.error && <ErrorBox text={[errorText(publish.error), ...fieldProblems(publish.error)].join(' — ')} />}
        </Dialog>
      )}
    </>
  );
}
